import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

const ChangeStaffCredentialsPage = () => {
    const { staffId } = useParams();
    const navigate = useNavigate();
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || "";

    const [staffName, setStaffName] = useState("");
    const [mobile, setMobile] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        const fetchStaff = async () => {
            try {
                setLoading(true);
                const res = await fetch(`${apiBaseUrl}/fetchstaff?staffId=${encodeURIComponent(staffId)}`);
                const data = await res.json();

                if (!res.ok) throw new Error(data.error || "Failed to load staff");

                setStaffName(data.companyName || "");
                setMobile(data.mobileNumber || "");
                setPassword(data.password || "");
            } catch (err) {
                setError(err.message || "Failed to load staff");
            } finally {
                setLoading(false);
            }
        };

        if (apiBaseUrl && staffId) fetchStaff();
    }, [apiBaseUrl, staffId]);

    const updateStaffName = async () => {
        const res = await fetch(`${apiBaseUrl}/updatestaffname`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ staffId, companyName: staffName.trim() }),
        });
        const data = await res.json();
        if (!res.ok || !data.success) throw new Error(data.error || "Failed to update staff name");
    };

    const updateMobile = async () => {
        const res = await fetch(`${apiBaseUrl}/updatestaffmobile`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ staffId, mobileNumber: mobile.trim() }),
        });
        const data = await res.json();
        if (!res.ok || !data.success) throw new Error(data.error || "Failed to update mobile");
    };

    const updatePassword = async () => {
        const res = await fetch(`${apiBaseUrl}/updatestaffpassword`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ staffId, password: password.trim() }),
        });
        const data = await res.json();
        if (!res.ok || (!data.success && !data.message)) {
            throw new Error(data.error || "Failed to update password");
        }
    };

    const handleSave = async () => {
        setError("");

        if (!staffName.trim()) {
            setError("Please enter staff name");
            return;
        }

        if (!/^\d{10}$/.test(mobile.trim())) {
            setError("Please enter a valid 10-digit mobile number");
            return;
        }

        if (!password.trim()) {
            setError("Please enter password");
            return;
        }

        try {
            setSaving(true);
            await updateStaffName();
            await updateMobile();
            await updatePassword();
            alert("Staff credentials updated successfully");
            navigate("/enablestaff");
        } catch (err) {
            setError(err.message || "Failed to update staff credentials");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
            <div className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-md">
                <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">Change Staff Credentials</h2>

                {error && <div className="mb-5 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">{error}</div>}

                {loading ? (
                    <p className="text-center text-gray-600">Loading staff...</p>
                ) : (
                    <div className="space-y-5">
                        <div>
                            <label className="block text-gray-700 font-medium mb-2">Staff Name</label>
                            <input
                                value={staffName}
                                onChange={(e) => setStaffName(e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Enter staff name"
                                disabled={saving}
                            />
                        </div>

                        <div>
                            <label className="block text-gray-700 font-medium mb-2">Mobile</label>
                            <input
                                type="tel"
                                value={mobile}
                                onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Enter mobile number"
                                maxLength={10}
                                disabled={saving}
                            />
                        </div>

                        <div>
                            <label className="block text-gray-700 font-medium mb-2">Password</label>
                            <input
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Enter password"
                                disabled={saving}
                            />
                        </div>

                        <button
                            onClick={handleSave}
                            disabled={saving}
                            className={`w-full py-3 px-4 text-white font-semibold rounded-lg transition ${
                                saving ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
                            }`}
                        >
                            {saving ? "Updating..." : "Update Credentials"}
                        </button>

                        <button
                            onClick={() => navigate(-1)}
                            disabled={saving}
                            className="w-full py-3 px-4 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition"
                        >
                            Cancel
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ChangeStaffCredentialsPage;
